import { useCallback, useEffect, useRef, useState } from 'react';
import { hostLogin, hostSignup, hostRefresh, hostMe, hostLogout } from '../api/host';
import {
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
} from '../utils/safeStorage';

const ACCESS_KEY = 'hostAccessToken';
const REFRESH_KEY = 'hostRefreshToken';
const isDev = process.env.NODE_ENV !== 'production';
const logDiag = (label, data) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log(`[host-auth] ${label}`, data);
  }
};

const setToken = (token) => {
  setSessionItem(ACCESS_KEY, token);
  setLocalItem(ACCESS_KEY, token);
};

const hasStoredRefreshToken = () => Boolean(getSessionItem(REFRESH_KEY) || getLocalItem(REFRESH_KEY));

export default function useHostAuth() {
  const [accessToken, setAccessToken] = useState(() => getSessionItem(ACCESS_KEY) || getLocalItem(ACCESS_KEY) || '');
  const [host, setHost] = useState(null);
  const [loading, setLoading] = useState(true);
  const bootstrapRun = useRef(0);
  const refreshTimer = useRef(null);
  const applyToken = useCallback(
    (token) => {
      if (!token) return;
      setToken(token);
      setAccessToken(token);
    },
    []
  );

  const bootstrap = useCallback(async () => {
    const runId = ++bootstrapRun.current;
    const isActive = () => bootstrapRun.current === runId;
    setLoading(true);
    const attemptRefresh = async () => {
      const refreshed = await hostRefresh();
      const nextToken = refreshed?.accessToken;
      if (!nextToken) return false;
      const me = await hostMe(nextToken, { skipRefresh: true });
      if (!isActive()) return true;
      applyToken(nextToken);
      setHost(me);
      logDiag('refreshed', { id: me?.id, status: me?.status });
      return true;
    };
    try {
      if (accessToken) {
        try {
          const me = await hostMe(accessToken, { skipRefresh: true });
          if (!isActive()) return;
          setHost(me);
          logDiag('session_restored', { id: me?.id, status: me?.status });
          return;
        } catch (err) {
          // access token may be expired; try refresh flow
          const refreshed = await attemptRefresh();
          if (refreshed) return;
          throw err;
        }
      }
      if (!hasStoredRefreshToken()) return;
      await attemptRefresh();
    } catch (err) {
      if (isActive()) {
        logDiag('bootstrap_failed', { message: err.message });
        removeSessionItem(ACCESS_KEY);
        removeLocalItem(ACCESS_KEY);
        removeSessionItem(REFRESH_KEY);
        removeLocalItem(REFRESH_KEY);
        setAccessToken('');
        setHost(null);
      }
    } finally {
      if (isActive()) setLoading(false);
    }
  }, [accessToken, applyToken]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    const handleExpired = () => {
      removeSessionItem(ACCESS_KEY);
      removeLocalItem(ACCESS_KEY);
      removeSessionItem(REFRESH_KEY);
      removeLocalItem(REFRESH_KEY);
      setAccessToken('');
      setHost(null);
    };
    const handleUpdated = (event) => {
      const nextToken = event?.detail?.token;
      if (nextToken && nextToken !== accessToken) {
        setAccessToken(nextToken);
      }
    };
    window.addEventListener('host-auth-expired', handleExpired);
    window.addEventListener('host-auth-updated', handleUpdated);
    return () => {
      window.removeEventListener('host-auth-expired', handleExpired);
      window.removeEventListener('host-auth-updated', handleUpdated);
    };
  }, [accessToken]);

  useEffect(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
    if (!accessToken) return () => {};

    const decodePayload = (token) => {
      if (!token || typeof token !== 'string') return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = raw.length % 4 ? '='.repeat(4 - (raw.length % 4)) : '';
      try {
        return JSON.parse(atob(raw + pad));
      } catch {
        return null;
      }
    };

    const payload = decodePayload(accessToken);
    const expMs = payload?.exp ? payload.exp * 1000 : 0;
    if (!expMs) return () => {};

    const delayMs = Math.max(expMs - Date.now() - 60000, 5000);
    let active = true;
    refreshTimer.current = setTimeout(async () => {
      if (!active) return;
      try {
        const refreshed = await hostRefresh();
        const nextToken = refreshed?.accessToken;
        if (!nextToken) throw new Error('Refresh failed');
        const me = await hostMe(nextToken, { skipRefresh: true });
        if (active) setHost(me);
        if (active) applyToken(nextToken);
      } catch {
        if (active) {
          removeSessionItem(ACCESS_KEY);
          removeLocalItem(ACCESS_KEY);
          removeSessionItem(REFRESH_KEY);
          removeLocalItem(REFRESH_KEY);
          setAccessToken('');
          setHost(null);
        }
      }
    }, delayMs);

    return () => {
      active = false;
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [accessToken, applyToken]);

  const login = async (payload) => {
    bootstrapRun.current += 1;
    const data = await hostLogin(payload);
    applyToken(data.accessToken);
    setHost(data.host);
    if (isDev) {
      try {
        const me = await hostMe(data.accessToken, { skipRefresh: true });
        logDiag('login_me_check', { id: me?.id, status: me?.status });
      } catch (err) {
        logDiag('login_me_check_failed', { message: err.message });
      }
    }
    return data.host;
  };

  const signup = async (payload) => {
    bootstrapRun.current += 1;
    const data = await hostSignup(payload);
    applyToken(data.accessToken);
    setHost(data.host);
    return data.host;
  };

  const logout = async () => {
    if (accessToken) {
      try {
        await hostLogout(accessToken);
      } catch {
        // ignore
      }
    }
    removeSessionItem(ACCESS_KEY);
    removeLocalItem(ACCESS_KEY);
    removeSessionItem(REFRESH_KEY);
    removeLocalItem(REFRESH_KEY);
    setAccessToken('');
    setHost(null);
  };

  return { accessToken, host, loading, login, signup, logout, setHost };
}
